import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { useAuth } from '../hooks/useAuth';
import { clearLocalLogout, isLocalLogout } from '../services/auth';
import { trackEvent } from '../services/telemetry';
import AuthStatusShell from './AuthStatusShell';

/**
 * Route guard for the admin SPA (docs/plans/admin-site-proposal.md,
 * section 5). While the auth profile is loading, shows a spinner; when
 * there is no signed-in user, redirects to the SWA login endpoint with
 * the intended page as the post-login redirect target.
 */
export default function ProtectedRoute() {
  const { isAuthenticated, loading, error, login, refresh } = useAuth();
  const location = useLocation();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInError, setSignInError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated && !loading) {
      trackEvent('admin_auth_redirect', { path: location.pathname });
    }
  }, [isAuthenticated, loading, location.pathname]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Spin size="large" tip="Checking sign-in status…" />
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="warning"
        title="Session check failed"
        subTitle={error}
        extra={<Button type="primary" onClick={() => login()}>Sign in</Button>}
      />
    );
  }

  if (!isAuthenticated) {
    if (isLocalLogout()) {
      const handleLocalSignIn = async () => {
        setIsSigningIn(true);
        setSignInError(null);
        clearLocalLogout();

        try {
          await refresh();
        } catch (err) {
          setSignInError(err?.message || 'Unable to restore the local session.');
          setIsSigningIn(false);
        }
      };

      return (
        <AuthStatusShell
          title="Your session is closed"
          description="You have been signed out of the admin workspace. Sign back in to continue managing your Journiy data."
          status="Sign-out complete"
          actionLabel={isSigningIn ? 'Restoring session' : 'Sign in again'}
          actionLoading={isSigningIn}
          onAction={handleLocalSignIn}
          footnote={import.meta.env.DEV ? 'Development workspace · Mock identity enabled' : null}
        />
      );
    }
    login();
    return null;
  }

  return <Outlet />;
}
